"use client";
import { AvailabilityComponent } from "@components/click-and-collect/AvailabilityComponent";
import { checkoutPage, shoppingBagPage } from "@lib/constants/pagePaths";
import { Box } from "@ecco/ui/box";
import { focusOutline } from "@ecco/ui/css/focus";
import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay } from "@ecco/ui/modal";
import { useDisclosure } from "@ecco/ui/use-disclosure";
import { use, useEffect, useState } from "react";
import { Address } from "./Address";
import type { StoreData } from "@features/stores/types";
import { Controller } from "./controller";
import { WalkInHours } from "./walk-in-hours";
import { isEccoStore } from "./utils/isEccoStore";
import { activeSiteConfig } from "@config/site/site-config";
import { ShippingMethodTypes } from "@features/checkout/types";
import { usePathname } from "next/navigation";
import { use2xlBreakpoint } from "@ecco/ui/use-breakpoint";
import { formatCityLine, formatStreet } from "./utils/formatAddress";
import type { ReactNode } from "react";

interface StoreModalProps {
    selectedVariant?: any;
    onClose?: () => void;
    renderStoreConfirmation?: (store?: StoreData) => ReactNode;
    shippingMethodKey: string;
}

export const StoreModal = ({
    selectedVariant,
    onClose: onCloseParent,
    renderStoreConfirmation,
    shippingMethodKey,
}: StoreModalProps) => {
    const { selectedStore, lastSelect, setLastSelect, isClickAndCollect } = use(Controller);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const pathname = usePathname();
    const isExtraLargeViewport = use2xlBreakpoint();
    const [store, setStore] = useState<StoreData>();
    const [localDate, setLocalDate] = useState<Date>();
    const { showParcelLockerId, hideParcelLockerWorkingHours } = activeSiteConfig;
    const isParcelShop = shippingMethodKey === ShippingMethodTypes.PARCEL_SHOP;
    const showParcelLockerInfo = isParcelShop && showParcelLockerId;
    const hideParcelShopWorkingHours = isParcelShop && hideParcelLockerWorkingHours;

    useEffect(() => {
        if (lastSelect !== 0) {
            setLastSelect(0);
            if (!isExtraLargeViewport) {
                onOpen();
            }
        }
    }, [lastSelect, onOpen, setLastSelect]);

    useEffect(() => {
        const store = selectedStore();
        if (store) {
            setStore(store);
            setLocalDate(new Date());
        }
    }, [selectedStore]);

    return isOpen ? (
        <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
            <ModalOverlay />
            <ModalContent
                mt={0}
                mb={0}
                alignSelf="flex-start"
                borderBottomLeftRadius="0"
                borderBottomRightRadius="0"
                marginInlineStart="auto"
            >
                <ModalCloseButton
                    _focusVisible={focusOutline}
                    top={0}
                    right={0}
                    sx={{
                        position: "absolute",
                        "--close-button-size": "48px",
                    }}
                />
                <ModalBody>
                    <Address
                        name={store?.name}
                        street={formatStreet(store)}
                        city={formatCityLine(store)}
                        walkInHours={store?.walkInHours}
                        localDate={localDate}
                        latitude={store?.latitude}
                        longitude={store?.longitude}
                        isModal
                        isExpanded
                        isEccoStore={isEccoStore(store)}
                        storeId={store?.storeId}
                        showParcelLockerInfo={showParcelLockerInfo}
                        hideParcelShopWorkingHours={hideParcelShopWorkingHours}
                    />
                    {!hideParcelShopWorkingHours && (
                        <WalkInHours
                            localDate={localDate}
                            walkInHours={store?.walkInHours}
                            name={store?.name}
                            street={formatStreet(store)}
                            postalCode={store?.postalCode}
                            city={store?.city}
                            phone={store?.phone}
                            latitude={store?.latitude}
                            longitude={store?.longitude}
                            isModal
                        />
                    )}
                    {renderStoreConfirmation ? (
                        <Box bg="transparent" mt={4}>
                            {renderStoreConfirmation(store)}
                        </Box>
                    ) : isClickAndCollect &&
                      !Object.values(checkoutPage).find((path) => pathname.includes(path)) &&
                      !Object.values(shoppingBagPage).find((path) => pathname.includes(path)) ? (
                        <Box bg="transparent" mt={4}>
                            <AvailabilityComponent
                                selectedVariant={selectedVariant}
                                onClose={onCloseParent}
                                store={store}
                            />
                        </Box>
                    ) : null}
                </ModalBody>
            </ModalContent>
        </Modal>
    ) : null;
};
